import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import {
  color as colorProps,
  compose,
  flexbox,
  layout,
  position,
  space,
} from 'styled-system';

import { forwardValidProp } from 'shopper/utils/style';

import APP from 'constants/app';

const SIZES = ['size1', 'size2', 'size3', 'size4', 'size5'];
const TYPES = ['primary', 'button'];
const ROLES = ['presentation', 'img'];
const VIEW_BOX = '0 0 24 24';

const iconSizeMap = {
  size1: '24px',
  size2: '20px',
  size3: '16px',
  size4: '12px',
  size5: '8px',
};

const baseStyles = ({ size }) => css`
  display: inline-flex;
  height: ${size};
  width: ${size};
`;

const StyledSvg = styled('svg', {
  shouldForwardProp: (prop) => forwardValidProp(prop, []),
})`
  ${baseStyles}
  ${compose(colorProps, layout, space, position, flexbox)}
  ${({ color }) => css`
    & > use {
      color: ${color + ' !important'};
    }
  `}
`;

const Icon = ({ title, type, name, size, role, color, ...rest }) => {
  const currSize =
    typeof size === 'string' && size.startsWith('size')
      ? iconSizeMap[size]
      : size;

  const component = (
    <StyledSvg
      viewBox={VIEW_BOX}
      role={role}
      size={currSize}
      color={color}
      {...rest}
    >
      {title && <title>{title}</title>}
      <use
        xlinkHref={`${APP.LOCAL_IMAGE_PATH}/sprites/spritemap.svg#sprite-${name}`}
      />
    </StyledSvg>
  );

  return type !== 'button' ? component : <button>{component}</button>;
};

Icon.SIZES = SIZES;
Icon.TYPES = TYPES;
Icon.displayName = 'Icon';

Icon.defaultProps = {
  role: 'presentation',
  size: 'size2',
  title: null,
  type: 'primary',
};

Icon.propTypes = {
  /**
   * Name of icon that to be rendered
   */
  name: PropTypes.string.isRequired,
  /**
   * Change the representation of icon in window. Default is `presentation`
   */
  role: PropTypes.oneOf(ROLES),
  /**
   * Set icon size
   */
  size: PropTypes.oneOf([PropTypes.oneOf(SIZES), PropTypes.arrayOf(SIZES)]),
  /**
   * TODO default titles can be internalized to improve DX
   *
   * Set the icon title for accessibility
   */
  title: PropTypes.string,
  /**
   * Changes the type of icon, can be choosing one of `primary` and `button`
   */
  type: PropTypes.oneOf(TYPES),
  /**
   * Implements `styled-system` [color](https://styled-system.com/api/#color) utility
   */
  ...propTypes.color,
  /**
   * Implements `styled-system` [flexbox](https://styled-system.com/api/#flexbox) utility
   */
  ...propTypes.flexbox,
  /**
   * Implements `styled-system` [layout](https://styled-system.com/api/#layout) utility
   */
  ...propTypes.layout,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [position](https://styled-system.com/api/#position) utility
   */
  ...propTypes.position,
};

export default Icon;
